import type { CSSProperties, ReactNode, TouchEvent } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { BasePlacement } from '@popperjs/core';

import { isTabletOrMobile } from '../../utils/devices/device';
import Bubble from './components/Bubble/Bubble';
import { Variant } from './types';
import { ChildrenWrapper, Wrapper, WrapperWithOverflow } from './Tooltip.styled';

export interface TooltipProps {
  /**
   * Property used for testing purposes.
   */
  dataCy?: string;
  /**
   * Text to display on the tooltip.
   */
  text: string;
  /**
   * Delay time until the tooltip appears tooltip.
   */
  delay?: number;
  /**
   * Preferable location of the tooltip if there is space available. It can be one of the following:
   * `Tooltip.Position.TOP`,`Tooltip.Position.BOTTOM`, `Tooltip.Position.RIGHT`, `Tooltip.Position.LEFT`.
   * More information at [Popper.js](https://popper.js.org/react-popper/v2/render-props/#placement).
   */
  placement?: BasePlacement;
  /**
   * List of placements that the tooltip will use if there is no space available in the preferable location,
   * it will try in order and use the first location where it finds space.
   * More information at [Popper.js](https://popper.js.org/docs/v2/modifiers/flip/#fallbackplacements)
   */
  fallbackPlacements?: BasePlacement[];
  /**
   * Area where the boundary of the tooltip is verified.
   * More information at [Popper.js](https://popper.js.org/docs/v2/utils/detect-overflow/#boundary)
   */
  boundary?: string | Element;
  /**
   * Node in which the tooltip will be rendered via react portal.
   */
  renderReferencePortalNode?: HTMLElement | null;
  children: ReactNode;
  /**
   * Reference to the element that triggers the tooltip.
   */
  tooltipTriggerReference?: HTMLElement | null;
  /**
   * Reference to the element on the basis of which the tooltip is positioned.
   */
  positionReference?: Element | null;
  /**
   * Establishes the z-order of the component.
   */
  zIndex?: number;
  /**
   * Describes the strategy of the position to be used.
   * It can be one of the following: "absolute", "fixed".
   * More information at [Popper.js](https://popper.js.org/docs/v2/constructors/#strategy)
   */
  tooltipPositionStrategy?: 'absolute' | 'fixed';
  /**
   * If true, the tooltip will be rendered with HTML tags.
   * If false, the tooltip will be rendered with plain text.
   */
  disableHtmlRendering?: boolean;
  /**
   * If true, the tooltip will break words by letters.
   * If false, the tooltip will break sentences by spaces.
   */
  breakAll?: boolean;
  /**
   * If true, the tooltip will never appear.
   * If false, the tooltip will work as usual.
   */
  disabled?: boolean;
  /**
   * Variant on which the style of the tooltip depends. Options are: `Tooltip.Variant.DARK`, `Tooltip.Variant.LIGHT`
   */
  variant?: Variant;
  /**
   * The width of the tooltip.
   */
  width?: string;
  /**
   * If true, the tooltip will use a wrapper with overflow hidden.
   * If false or undefined, it will use the default wrapper without overflow hidden.
   */
  overflowHidden?: boolean;
}

const DEFAULT_DELAY = 300;

const defaultFallbackPlacements = [
  Bubble.BubblePosition.TOP,
  Bubble.BubblePosition.BOTTOM,
  Bubble.BubblePosition.RIGHT,
  Bubble.BubblePosition.LEFT,
];

const containLink = (text: string) => text.includes('href=');

const containHtml = (text: string) => text.includes('<');

const Tooltip = ({
  text,
  delay = DEFAULT_DELAY,
  placement = Bubble.BubblePosition.TOP,
  fallbackPlacements = defaultFallbackPlacements,
  boundary = 'clippingParents',
  renderReferencePortalNode,
  children,
  dataCy,
  zIndex,
  tooltipTriggerReference,
  positionReference,
  tooltipPositionStrategy = 'fixed',
  disableHtmlRendering = false,
  breakAll = false,
  disabled = false,
  variant = Variant.DARK,
  width,
  overflowHidden = false,
}: TooltipProps) => {
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);
  const hoverDelayTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();
  const longTouchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>();

  const getTargetRef = useCallback(
    () => tooltipTriggerReference ?? ref.current,
    [tooltipTriggerReference, ref],
  );

  const clearLongTouch = () => {
    if (longTouchTimeout.current) {
      clearTimeout(longTouchTimeout.current);
      longTouchTimeout.current = undefined;
    }
  };

  const startLongTouch = useCallback(() => {
    clearLongTouch();

    longTouchTimeout.current = setTimeout(() => {
      setShow(true);
    }, delay);
  }, [delay]);

  const clearHoverDelay = () => {
    if (hoverDelayTimeout.current) {
      clearTimeout(hoverDelayTimeout.current);
      hoverDelayTimeout.current = undefined;
    }
  };

  const startHoverDelay = useCallback(() => {
    clearHoverDelay();

    hoverDelayTimeout.current = setTimeout(() => {
      setShow(true);
    }, delay);
  }, [delay]);

  useEffect(() => {
    return () => {
      clearHoverDelay();
      clearLongTouch();
    };
  }, []);

  const hide = useCallback(() => {
    setShow(false);
    clearHoverDelay();
    getTargetRef()?.blur();
  }, [getTargetRef]);

  useEffect(() => {
    if (show) {
      window.addEventListener('scroll', hide, true);
    }

    return () => {
      window.removeEventListener('scroll', hide, true);
    };
  }, [show, hide]);

  useEffect(() => {
    const targetRef = getTargetRef();

    if (!targetRef) {
      return undefined;
    }

    const handleDisplayBasedOnTextContain = () => {
      if (!containLink(text)) {
        hide();
      }
    };

    interface Event {
      event: keyof HTMLElementEventMap;
      handler: EventListener;
      isPassive?: boolean;
    }

    const listEvents = [
      {
        event: 'mouseenter',
        handler() {
          if (!isTabletOrMobile) {
            startHoverDelay();
          }
        },
      },
      {
        event: 'mouseleave',
        handler() {
          handleDisplayBasedOnTextContain();
        },
      },
      {
        event: 'touchstart',
        handler() {
          startLongTouch();
          hide();
        },
        isPassive: true,
      },
      {
        event: 'touchend',
        handler(event: TouchEvent<HTMLElement>) {
          if (isTabletOrMobile && show) {
            event.preventDefault();
          }

          clearLongTouch();
          handleDisplayBasedOnTextContain();
        },
      },
      {
        event: 'focus',
        handler() {
          if (!isTabletOrMobile) {
            setShow(true);
          }
        },
      },
      {
        event: 'blur',
        handler() {
          hide();
        },
      },
    ] as Event[];

    listEvents.forEach(({ event, handler, isPassive }) => {
      targetRef.addEventListener(event, handler, isPassive ? { passive: true } : false);
    });

    return () => {
      listEvents.forEach(({ event, handler }) => {
        targetRef.removeEventListener(event, handler);
      });
    };
  }, [hide, show, startHoverDelay, startLongTouch, text, getTargetRef]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        hide();
      }
    },
    [hide],
  );

  useEffect(() => {
    if (show) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [show, handleKeyDown]);

  useEffect(() => {
    if (show) {
      hide();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);

  const handleMouseLeave = hide;

  const style: CSSProperties = useMemo(
    () => ({
      ...(!containLink(text) ? { pointerEvents: 'none' } : {}),
      ...(zIndex ? { zIndex } : {}),
      ...(!show ? { visibility: 'hidden' } : {}),
    }),
    [show, text, zIndex],
  );

  const WrapperComponent = overflowHidden ? WrapperWithOverflow : Wrapper;

  return (
    <WrapperComponent onMouseLeave={handleMouseLeave}>
      <ChildrenWrapper ref={ref}>{children}</ChildrenWrapper>
      <Bubble
        text={text}
        dataCy={dataCy}
        placement={placement}
        referenceElement={positionReference ? { current: positionReference } : ref}
        fallbackPlacements={fallbackPlacements}
        show={!disabled && show}
        boundary={boundary}
        renderReferencePortalNode={renderReferencePortalNode ?? undefined}
        strategy={tooltipPositionStrategy}
        scrollListener={false}
        containHtml={containLink(text) || containHtml(text)}
        disableHtmlRendering={disableHtmlRendering}
        breakAll={breakAll}
        style={style}
        variant={variant}
        width={width}
      />
    </WrapperComponent>
  );
};

Tooltip.Position = Bubble.BubblePosition;
Tooltip.Variant = Variant;

export default Tooltip;
